import { GridTableFilterRow, HeaderData } from "../../../ui-components/GridTable/GridTableFilterRow";
import React from "react";

export type Range<T> = [T | null, T | null];

export type RangeCriteria<TCriteriaKey extends string, TItemValue> = { [key in TCriteriaKey]: Range<TItemValue> };
export type RangeFilterChangeFunction<TCriteriaKey extends string, TItemValue> = (
  key: TCriteriaKey,
  value: Range<TItemValue>,
) => void;

export const rangeFilter =
  <TCriteriaKey extends string, TItemKey extends string, TItemValue>(
    criteriaKey: TCriteriaKey,
    itemKey: TItemKey,
    comparator: (a: TItemValue, b: TItemValue) => number,
  ) =>
  (item: { [key in TItemKey]: TItemValue }, constrains: RangeCriteria<TCriteriaKey, TItemValue>) => {
    const left = constrains[criteriaKey][0];
    const right = constrains[criteriaKey][1];

    return (
      (left === null || comparator(left, item[itemKey]) <= 0) &&
      (right === null || comparator(right, item[itemKey]) >= 0)
    );
  };

export const RangeFilterComponent = <TCriteriaKey extends string, TItemValue>({
  criteriaKey,
  criteria,
  onFilterChange,
  createComponent,
}: {
  criteriaKey: TCriteriaKey;
  criteria: RangeCriteria<TCriteriaKey, TItemValue>;
  onFilterChange: RangeFilterChangeFunction<TCriteriaKey, TItemValue>;
  createComponent: (data: {
    value: Range<TItemValue>;
    onChange: (value: Range<TItemValue>) => void;
  }) => React.ReactNode;
}) =>
  createComponent({
    value: criteria[criteriaKey],
    onChange: (value) => onFilterChange(criteriaKey, value),
  });

export const RangeColumnDefinition = <
  TCriteriaKey extends string,
  TItemKey extends string,
  TCriteriaValue,
  TItemValue,
  TRow extends {
    [key in TItemKey]: TItemValue;
  },
>(
  name: React.ReactNode,
  itemKey: TItemKey,
  criteriaKey: TCriteriaKey,
  criteria: RangeCriteria<TCriteriaKey, TCriteriaValue>,
  onFilterChange: RangeFilterChangeFunction<TCriteriaKey, TCriteriaValue>,
  filterRenderer: (data: {
    criteriaKey: TCriteriaKey;
    criteria: RangeCriteria<TCriteriaKey, TCriteriaValue>;
    onFilterChange: RangeFilterChangeFunction<TCriteriaKey, TCriteriaValue>;
  }) => React.ReactNode,
  itemRenderer: (item: TRow) => React.ReactNode,
  width?: number,
) => ({
  key: itemKey,
  name: <>{name}</>,
  headerCellClass: "GridTableFilterRow__header-cell",
  formatter: ({ row }: { row: TRow }) => itemRenderer(row),
  headerRenderer: (header: HeaderData) => (
    <GridTableFilterRow header={header}>
      {filterRenderer({ criteriaKey, criteria, onFilterChange })}
    </GridTableFilterRow>
  ),
  width: width ?? 320,
  sortable: true,
});
